/* Maison Rouleur — custom layer on top of Impulse.
   Modules: motion, countdown, weather, size finder, bundle picker,
   cart extras, sticky ATC, delivery estimate. Vanilla JS, no deps. */
(function () {
  'use strict';

  var MR = window.MR = window.MR || {};
  var S = MR.settings || {};
  var T = MR.t || {};
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function money(cents) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, (window.theme.settings && theme.settings.moneyFormat) || S.moneyFormat);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function fmt(str, map) {
    return String(str).replace(/\[(\w+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function store(key, val) {
    try {
      if (val === undefined) return JSON.parse(localStorage.getItem(key) || 'null');
      localStorage.setItem(key, JSON.stringify(val));
    } catch (e) { return null; }
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* --------------------------------------------------------------- motion */
  MR.motion = {
    init: function () {
      this.split();
      this.reveal();
      this.draw();
      this.counters();
      this.parallax();
    },

    // Split headline words into masked spans for a kinetic rise-in.
    split: function () {
      $$('[data-mr-split]:not(.mr-split-done)').forEach(function (el) {
        var words = el.textContent.trim().split(/\s+/);
        el.setAttribute('aria-label', el.textContent.trim());
        el.innerHTML = words.map(function (w, i) {
          return '<span class="mr-w" aria-hidden="true"><span class="mr-w__i" style="--i:' + i + '">' + w + '</span></span>';
        }).join(' ');
        el.classList.add('mr-split-done');
      });
    },

    reveal: function () {
      var els = $$('[data-mr-reveal], [data-mr-split], [data-mr-stagger]');
      if (!('IntersectionObserver' in window) || reduceMotion) {
        els.forEach(function (el) { el.classList.add('is-in'); });
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
      els.forEach(function (el) {
        if (el.hasAttribute('data-mr-stagger')) {
          $$(':scope > *', el).forEach(function (c, i) { c.style.setProperty('--i', i); });
        }
        io.observe(el);
      });
    },

    // Elevation-profile lines: stroke draws with scroll progress through the viewport.
    draw: function () {
      var paths = $$('[data-mr-draw]');
      if (!paths.length) return;
      paths.forEach(function (p) {
        var len = p.getTotalLength ? p.getTotalLength() : 1000;
        p.style.strokeDasharray = len;
        p.style.strokeDashoffset = reduceMotion ? 0 : len;
        p._len = len;
      });
      if (reduceMotion) return;
      var ticking = false;
      function update() {
        var vh = window.innerHeight;
        paths.forEach(function (p) {
          var host = p.closest('[data-mr-draw-host]') || p.ownerSVGElement || p;
          var r = host.getBoundingClientRect();
          var mode = p.getAttribute('data-mr-draw');
          var prog;
          if (mode === 'load') {
            prog = 1;
          } else {
            prog = (vh - r.top) / (vh * 0.75 + r.height * 0.5);
          }
          prog = Math.max(0, Math.min(1, prog));
          p.style.strokeDashoffset = p._len * (1 - prog);
          var dot = p.parentNode.querySelector('[data-mr-draw-dot]');
          if (dot && p.getPointAtLength) {
            var pt = p.getPointAtLength(p._len * prog);
            dot.setAttribute('cx', pt.x);
            dot.setAttribute('cy', pt.y);
            dot.style.opacity = prog > 0.01 ? 1 : 0;
          }
        });
        ticking = false;
      }
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
    },

    counters: function () {
      var els = $$('[data-mr-count]');
      if (!els.length) return;
      function run(el) {
        var target = parseFloat(el.getAttribute('data-mr-count'));
        var dec = (String(target).split('.')[1] || '').length;
        if (reduceMotion) { el.textContent = target.toFixed(dec).replace('.', ','); return; }
        var start = performance.now();
        var dur = 1600;
        (function tick(now) {
          var t = Math.min(1, (now - start) / dur);
          var e = 1 - Math.pow(1 - t, 4);
          el.textContent = (target * e).toFixed(dec).replace('.', ',');
          if (t < 1) requestAnimationFrame(tick);
        })(start);
      }
      if (!('IntersectionObserver' in window)) { els.forEach(run); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { run(e.target); io.unobserve(e.target); }
        });
      }, { threshold: 0.4 });
      els.forEach(function (el) { io.observe(el); });
    },

    parallax: function () {
      var els = $$('[data-mr-parallax]');
      if (!els.length || reduceMotion) return;
      var ticking = false;
      function update() {
        var vh = window.innerHeight;
        els.forEach(function (el) {
          var host = el.closest('[data-mr-parallax-host], section, .mr-section') || el.parentNode;
          var r = host.getBoundingClientRect();
          if (r.bottom < 0 || r.top > vh) return;
          var speed = parseFloat(el.getAttribute('data-mr-parallax')) || 0.15;
          var center = r.top + r.height / 2 - vh / 2;
          el.style.transform = 'translate3d(0,' + (center * -speed).toFixed(1) + 'px,0) scale(1.12)';
        });
        ticking = false;
      }
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
      update();
    }
  };

  /* ------------------------------------------------------------ countdown */
  MR.countdown = {
    init: function () {
      $$('[data-mr-countdown]').forEach(function (el) {
        var end = new Date(el.getAttribute('data-mr-countdown') || S.saleEnd);
        var daily = S.saleDaily && el.getAttribute('data-mr-countdown-fixed') === null;
        if (daily) {
          /* evergreen: always ends tonight at 00:00 (visitor's local time) */
          end = new Date(); end.setHours(24, 0, 0, 0);
          $$('[data-d]', el).forEach(function (d) { var u = d.closest('.mr-countdown__unit, .mr-cd-d'); (u || d).style.display = 'none'; });
          el.classList.add('is-daily');
        }
        if (isNaN(end.getTime())) { el.hidden = true; return; }
        var parts = {
          d: $('[data-d]', el), h: $('[data-h]', el), m: $('[data-m]', el), s: $('[data-s]', el)
        };
        function pad(n) { return n < 10 ? '0' + n : String(n); }
        function tick() {
          var diff = Math.max(0, end.getTime() - Date.now());
          if (diff <= 0 && daily) { end = new Date(); end.setHours(24, 0, 0, 0); diff = end.getTime() - Date.now(); }
          if (diff <= 0) {
            el.classList.add('is-ended');
            var hideTarget = el.closest('[data-mr-hide-when-ended]');
            if (hideTarget) hideTarget.hidden = true;
            clearInterval(timer);
          }
          var d = Math.floor(diff / 864e5);
          var h = Math.floor(diff / 36e5) % 24;
          var m = Math.floor(diff / 6e4) % 60;
          var s = Math.floor(diff / 1e3) % 60;
          if (parts.d) parts.d.textContent = pad(d);
          if (parts.h) parts.h.textContent = pad(h);
          if (parts.m) parts.m.textContent = pad(m);
          if (parts.s) parts.s.textContent = pad(s);
          var bar = el.closest('.mr-pdp-sale'); bar = bar && bar.querySelector('[data-mr-daybar]');
          if (bar && daily) bar.style.width = Math.max(4, Math.min(100, diff / 864e5 * 100)) + '%';
        }
        var timer = setInterval(tick, 1000);
        tick();
      });
    }
  };

  /* -------------------------------------------------------------- weather */
  // Highlights the "shop by temperature" card that matches this morning's
  // temperature (Open-Meteo, no key, no personal data: fixed city coordinates).
  MR.weather = {
    init: function () {
      var host = $('[data-mr-weather]');
      if (!host) return;
      var lat = host.getAttribute('data-lat') || S.weatherLat;
      var lon = host.getAttribute('data-lon') || S.weatherLon;
      var city = host.getAttribute('data-city') || S.weatherCity;
      var cache = store('mr_weather');
      if (cache && cache.lat === lat && Date.now() - cache.ts < 30 * 60 * 1000) {
        this.apply(host, cache.temp, city);
        return;
      }
      var url = 'https://api.open-meteo.com/v1/forecast?latitude=' + encodeURIComponent(lat) +
        '&longitude=' + encodeURIComponent(lon) + '&current=temperature_2m&timezone=auto';
      fetch(url).then(function (r) { return r.json(); }).then(function (data) {
        if (!data || !data.current) return;
        var temp = Math.round(data.current.temperature_2m);
        store('mr_weather', { lat: lat, temp: temp, ts: Date.now() });
        MR.weather.apply(host, temp, city);
      }).catch(function () {});
    },
    apply: function (host, temp, city) {
      var out = $('[data-mr-weather-out]', host);
      var key = temp < 5 ? 'froid' : temp < 12 ? 'frais' : temp < 20 ? 'doux' : 'chaud';
      if (out) {
        out.innerHTML = '<span class="mr-weather__dot mr-temp--' + key + '"></span>' +
          'Il fait <strong>' + temp + '°C</strong> à ' + city + ' en ce moment.';
        out.hidden = false;
      }
      $$('[data-mr-temp-card]', host).forEach(function (c) {
        c.classList.toggle('is-today', c.getAttribute('data-mr-temp-card') === key);
      });
    }
  };

  /* ---------------------------------------------------------- size finder */
  var SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  var CHART = {
    homme: { h: [165, 170, 175, 180, 185, 190, 196], w: [55, 63, 70, 78, 86, 95, 105] },
    femme: { h: [155, 160, 165, 170, 175, 180, 186], w: [48, 55, 62, 70, 78, 86, 95] }
  };

  function bucket(val, steps) {
    for (var i = 0; i < steps.length; i++) { if (val < steps[i]) return i; }
    return steps.length;
  }

  MR.size = {
    compute: function (a) {
      var c = CHART[a.gender] || CHART.homme;
      var hi = bucket(a.height, c.h);
      var wi = bucket(a.weight, c.w);
      // Weight drives fit of compression kit more than height; blend with a bias to the larger.
      var base = Math.max(wi, Math.round(hi * 0.4 + wi * 0.6));
      if (a.build === 'fort') base += 1;
      if (a.build === 'fin' && base > wi) base -= 1;
      if (a.fit === 'confort') base += 1;
      base = Math.max(0, Math.min(SIZES.length - 1, base));
      var spread = Math.abs(hi - wi);
      var confidence = spread <= 1 ? 96 : spread === 2 ? 89 : 81;
      return { size: SIZES[base], index: base, confidence: confidence };
    },
    get: function () { return store('mr_size'); },
    save: function (data) {
      store('mr_size', data);
      document.dispatchEvent(new CustomEvent('mr:size', { detail: data }));
    },

    initQuiz: function (root) {
      if (root._mrQuiz) return;
      root._mrQuiz = true;
      var steps = $$('[data-step]', root);
      var bar = $('[data-mr-quiz-bar]', root);
      var answers = { gender: 'homme', height: 176, weight: 74, build: 'moyen', fit: 'ajuste' };
      var saved = MR.size.get();
      if (saved && saved.answers) answers = Object.assign(answers, saved.answers);
      var current = 0;

      function show(i) {
        current = Math.max(0, Math.min(steps.length - 1, i));
        steps.forEach(function (s, n) {
          s.hidden = n !== current;
          s.classList.toggle('is-active', n === current);
        });
        if (bar) bar.style.transform = 'scaleX(' + ((current + 1) / steps.length) + ')';
        var focusable = $('input, button', steps[current]);
        if (focusable && root.closest('[open], .is-open, [data-mr-quiz-page]')) {
          try { focusable.focus({ preventScroll: true }); } catch (e) {}
        }
      }

      // choice buttons
      $$('[data-answer]', root).forEach(function (btn) {
        var k = btn.getAttribute('data-key');
        if (answers[k] === btn.getAttribute('data-answer')) btn.classList.add('is-selected');
        btn.addEventListener('click', function () {
          answers[k] = btn.getAttribute('data-answer');
          $$('[data-key="' + k + '"]', root).forEach(function (b) { b.classList.toggle('is-selected', b === btn); });
          setTimeout(function () { show(current + 1); }, 220);
        });
      });

      // range sliders
      $$('input[type="range"][data-key]', root).forEach(function (r) {
        var k = r.getAttribute('data-key');
        var out = $('[data-out="' + k + '"]', root);
        r.value = answers[k];
        function sync() {
          answers[k] = parseInt(r.value, 10);
          if (out) out.textContent = r.value;
          var pct = (r.value - r.min) / (r.max - r.min) * 100;
          r.style.setProperty('--p', pct + '%');
        }
        r.addEventListener('input', sync);
        sync();
      });

      $$('[data-next]', root).forEach(function (b) { b.addEventListener('click', function () { show(current + 1); }); });
      $$('[data-prev]', root).forEach(function (b) { b.addEventListener('click', function () { show(current - 1); }); });

      $$('[data-mr-quiz-submit]', root).forEach(function (b) {
        b.addEventListener('click', function () {
          var res = MR.size.compute(answers);
          var data = { size: res.size, confidence: res.confidence, answers: answers, ts: Date.now() };
          MR.size.save(data);
          var resultStep = $('[data-step="result"]', root);
          $$('[data-mr-result-size]', root).forEach(function (el) { el.textContent = res.size; });
          $$('[data-mr-result-conf]', root).forEach(function (el) { el.textContent = res.confidence; });
          $$('[data-mr-result-note]', root).forEach(function (el) {
            el.textContent = answers.fit === 'confort'
              ? 'Coupe confort : un peu d’aisance au buste et aux cuisses, idéale pour les longues sorties.'
              : 'Coupe ajustée : seconde peau, aucun pli au vent — comme les pros.';
          });
          show(steps.indexOf(resultStep));
          resultStep.classList.add('is-celebrate');
        });
      });

      $$('[data-mr-quiz-restart]', root).forEach(function (b) {
        b.addEventListener('click', function () { show(0); });
      });

      $$('[data-mr-apply-size]', root).forEach(function (b) {
        b.addEventListener('click', function () {
          MR.size.applyToProduct(true);
          MR.modal.close();
        });
      });

      show(0);
    },

    // Show saved size on PDP and preselect it when that size exists & is in stock.
    applyToProduct: function (force) {
      var data = MR.size.get();
      $$('[data-mr-size-badge]').forEach(function (b) {
        if (data && data.size) {
          $('[data-mr-size-value]', b).textContent = data.size;
          b.hidden = false;
        }
      });
      if (!data || !data.size) return;
      $$('fieldset.variant-input-wrap').forEach(function (fs) {
        var name = (fs.getAttribute('name') || '').toLowerCase();
        if (!/taille|size|maat/.test(name)) return;
        var input = $('input[type="radio"][value="' + data.size + '"]', fs);
        if (!input) return;
        var wrap = input.closest('.variant-input');
        if (wrap) wrap.classList.add('mr-is-recommended');
        if (input.classList.contains('disabled')) return;
        if (force || !fs.hasAttribute('data-mr-touched')) {
          if (!input.checked) input.click();
        }
      });
    },

    initProduct: function () {
      $$('fieldset.variant-input-wrap').forEach(function (fs) {
        fs.addEventListener('change', function (e) {
          if (e.isTrusted) fs.setAttribute('data-mr-touched', '1');
        });
      });
      MR.size.applyToProduct(false);
      // No quiz size yet: start on M instead of the smallest size (fewer wrong-size orders).
      if (!MR.size.get()) {
        $$('fieldset.variant-input-wrap').forEach(function (fs) {
          var name = (fs.getAttribute('name') || '').toLowerCase();
          if (!/taille|size|maat/.test(name)) return;
          var checked = $('input[type="radio"]:checked', fs);
          var m = $('input[type="radio"][value="M"]', fs);
          if (m && checked && /^(XS|S)$/.test(checked.value) && !m.classList.contains('disabled')) m.click();
        });
      }
      document.addEventListener('mr:size', function () { MR.size.applyToProduct(false); });
    }
  };

  /* ---------------------------------------------------------------- modal */
  MR.modal = {
    el: null,
    open: function (name) {
      var m = $('[data-mr-modal="' + name + '"]');
      if (!m) {
        if (name === 'size' && S.quizUrl) window.location.href = S.quizUrl;
        return;
      }
      this.el = m;
      this.lastFocus = document.activeElement;
      m.hidden = false;
      requestAnimationFrame(function () { m.classList.add('is-open'); });
      document.documentElement.classList.add('mr-lock');
      var quiz = $('[data-mr-quiz]', m);
      if (quiz) MR.size.initQuiz(quiz);
      var first = $('button, input, a', m);
      if (first) first.focus({ preventScroll: true });
    },
    close: function () {
      var m = this.el;
      if (!m) return;
      m.classList.remove('is-open');
      document.documentElement.classList.remove('mr-lock');
      setTimeout(function () { m.hidden = true; }, 350);
      if (this.lastFocus) this.lastFocus.focus({ preventScroll: true });
      this.el = null;
    },
    init: function () {
      document.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-mr-open]');
        if (opener) { e.preventDefault(); MR.modal.open(opener.getAttribute('data-mr-open')); return; }
        if (e.target.closest('[data-mr-close]')) { e.preventDefault(); MR.modal.close(); }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && MR.modal.el) MR.modal.close();
      });
    }
  };

  /* ------------------------------------------------------- bundle picker */
  MR.bundle = {
    init: function () {
      $$('[data-mr-bundle]').forEach(function (root) { MR.bundle.setup(root); });
      document.addEventListener('submit', MR.bundle.onSubmit, true);
    },

    setup: function (root) {
      if (root._mr) return;
      var json = $('[data-mr-product-json]', root);
      if (!json) return;
      var product = JSON.parse(json.textContent);
      var form = document.getElementById(root.getAttribute('data-form-id'));
      var state = { root: root, product: product, form: form, qty: 1 };
      root._mr = state;

      $$('input[name^="mr-bundle-"]', root).forEach(function (inp) {
        if (inp.checked) state.qty = parseInt(inp.value, 10);
        inp.addEventListener('change', function () {
          var prevQty = state.qty;
          state.qty = parseInt(inp.value, 10);
          if (state.qty > prevQty && state.qty > 1) MR.fx.burst(inp.closest('.mr-tier'), { n: 14 + state.qty * 4 });
          $$('.mr-tier', root).forEach(function (t) { t.classList.toggle('is-selected', $('input', t).checked); });
          MR.bundle.renderUnits(state);
          MR.bundle.updatePrices(state);
          MR.bundle.syncButton(state);
        });
      });

      document.addEventListener('variant:change', function () {
        setTimeout(function () {
          MR.bundle.updatePrices(state);
          MR.bundle.renderUnits(state, true);
          MR.bundle.syncButton(state);
        }, 0);
      });

      MR.bundle.renderUnits(state);
      MR.bundle.updatePrices(state);
      MR.bundle.syncButton(state);
    },

    currentVariant: function (state) {
      var id = state.form ? (state.form.querySelector('[name="id"]') || {}).value : null;
      var v = null;
      state.product.variants.forEach(function (x) { if (String(x.id) === String(id)) v = x; });
      return v || state.product.variants[0];
    },

    tierPct: function (qty) {
      var pct = 0;
      (S.tiers || []).forEach(function (t) { if (qty >= t.qty) pct = t.pct; });
      return pct;
    },

    updatePrices: function (state) {
      var v = MR.bundle.currentVariant(state);
      $$('.mr-tier', state.root).forEach(function (t) {
        var q = parseInt(t.getAttribute('data-qty'), 10);
        var pct = MR.bundle.tierPct(q);
        /* bundle display prices end in ,95 (nearest) — the single piece keeps its real price */
        var r95 = function (c) { if (q === 1) return c; return Math.round((c - 95) / 100) * 100 + 95; };
        var total = r95(Math.round(v.price * q * (1 - pct / 100)));
        var compare = r95((v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price) * q);
        var priceEl = $('[data-mr-tier-price]', t);
        var cmpEl = $('[data-mr-tier-compare]', t);
        var eachEl = $('[data-mr-tier-each]', t);
        var saveEl = $('[data-mr-tier-save]', t);
        var giftEl = $('[data-mr-tier-gift]', t);
        if (priceEl) priceEl.textContent = money(total);
        if (cmpEl) cmpEl.textContent = compare > total ? money(compare) : '';
        if (eachEl) eachEl.textContent = money(r95(Math.round(total / q))) + ' / pièce';
        if (saveEl) saveEl.textContent = compare > total ? '−' + money(compare - total) : '';
        if (giftEl) giftEl.hidden = !(S.giftThreshold && total >= S.giftThreshold);
        if (v.image) $$('[data-mr-tier-img]', t).forEach(function (img) { img.src = v.image; });
      });
      var pctNow = MR.bundle.tierPct(state.qty);
      var maxPct = (S.tiers && S.tiers.length) ? S.tiers[S.tiers.length - 1].pct : 20;
      var lbl = $('[data-mr-bundle-pct]', state.root);
      var fill = $('[data-mr-bundle-fill]', state.root);
      if (lbl) lbl.textContent = pctNow ? '−' + pctNow : '0';
      if (fill) fill.style.transform = 'scaleX(' + (maxPct ? pctNow / maxPct : 0) + ')';
      state.root.classList.toggle('has-discount', pctNow > 0);
    },

    optionKind: function (name) {
      var n = (name || '').toLowerCase();
      if (/couleur|color|colour|kleur|farbe/.test(n)) return 'color';
      return 'pill';
    },

    imageForValue: function (state, oi, val) {
      var img = null;
      state.product.variants.forEach(function (v) { if (!img && v.options[oi] === val && v.image) img = v.image; });
      return img;
    },

    // Extra pieces (2..n): image swatches for colour, pills for size/other options.
    renderUnits: function (state, keepChoices) {
      var wrap = $('[data-mr-units]', state.root);
      if (!wrap) return;
      var p = state.product;
      var hasOptions = !(p.variants.length === 1 && /default/i.test(p.variants[0].title));
      var main = MR.bundle.currentVariant(state);
      var prev = keepChoices ? MR.bundle.readUnits(state) : [];
      if (state.qty <= 1 || !hasOptions) { wrap.innerHTML = ''; wrap.hidden = true; return; }

      function esc(x) { return String(x).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
      var html = '<div class="mr-units__head"><b>Composez votre pack</b><span>Pièce 1 = votre sélection ci-dessus</span></div>';
      for (var n = 2; n <= state.qty; n++) {
        var chosenAll = prev[n - 2] || main.options;
        var vNow = MR.bundle.findVariant(state, chosenAll) || main;
        html += '<div class="mr-unit" data-unit="' + n + '" style="--i:' + (n - 2) + '">' +
          '<div class="mr-unit__top"><span class="mr-unit__img"><img src="' + esc(vNow.image || '') + '" alt="" width="56" height="56" data-mr-unit-img></span>' +
          '<span class="mr-unit__n">Pièce ' + n + '<small data-mr-unit-label>' + esc(chosenAll.join(' · ')) + '</small></span></div>';
        p.options.forEach(function (opt, oi) {
          var kind = MR.bundle.optionKind(opt.name);
          var chosen = chosenAll[oi];
          html += '<div class="mr-unit__opt mr-unit__opt--' + kind + '" data-opt="' + oi + '" data-val="' + esc(chosen) + '" role="radiogroup" aria-label="' + esc(opt.name) + '">';
          opt.values.forEach(function (val) {
            var sel = val === chosen;
            if (kind === 'color') {
              var im = MR.bundle.imageForValue(state, oi, val);
              html += '<button type="button" class="mr-sw' + (sel ? ' is-on' : '') + '" data-v="' + esc(val) + '" title="' + esc(val) + '" aria-pressed="' + sel + '">' +
                (im ? '<img src="' + esc(im) + '" alt="' + esc(val) + '" width="44" height="44">' : '<span>' + esc(val) + '</span>') + '</button>';
            } else {
              html += '<button type="button" class="mr-pill' + (sel ? ' is-on' : '') + '" data-v="' + esc(val) + '" aria-pressed="' + sel + '">' + esc(val) + '</button>';
            }
          });
          html += '</div>';
        });
        html += '</div>';
      }
      wrap.innerHTML = html;
      wrap.hidden = false;
      $$('.mr-unit__opt', wrap).forEach(function (grp) {
        grp.addEventListener('click', function (e) {
          var b = e.target.closest('button[data-v]');
          if (!b) return;
          grp.setAttribute('data-val', b.getAttribute('data-v'));
          $$('button', grp).forEach(function (x) { var on = x === b; x.classList.toggle('is-on', on); x.setAttribute('aria-pressed', on); });
          var unit = grp.closest('.mr-unit');
          var opts = $$('.mr-unit__opt', unit).map(function (g) { return g.getAttribute('data-val'); });
          var vv = MR.bundle.findVariant(state, opts);
          var im = $('[data-mr-unit-img]', unit);
          if (vv && vv.image && im) im.src = vv.image;
          var lb = $('[data-mr-unit-label]', unit);
          if (lb) lb.textContent = opts.join(' · ');
          MR.bundle.validateUnits(state);
        });
      });
      MR.bundle.validateUnits(state);
    },

    readUnits: function (state) {
      return $$('.mr-unit', state.root).map(function (u) {
        return $$('.mr-unit__opt', u).map(function (g) { return g.getAttribute('data-val'); });
      });
    },

    findVariant: function (state, opts) {
      var found = null;
      state.product.variants.forEach(function (v) {
        if (v.options.length === opts.length && v.options.every(function (o, i) { return o === opts[i]; })) found = v;
      });
      return found;
    },

    validateUnits: function (state) {
      var ok = true;
      $$('.mr-unit', state.root).forEach(function (u) {
        var opts = $$('.mr-unit__opt', u).map(function (g) { return g.getAttribute('data-val'); });
        var v = MR.bundle.findVariant(state, opts);
        var bad = !v || !v.available;
        u.classList.toggle('is-unavailable', bad);
        if (bad) ok = false;
      });
      state.unitsOk = ok;
      MR.bundle.syncButton(state);
      return ok;
    },

    syncButton: function (state) {
      if (!state.form) return;
      var btnText = state.form.querySelector('[data-add-to-cart-text]');
      var btn = state.form.querySelector('[data-add-to-cart]');
      if (!btn || !btnText) return;
      var v = MR.bundle.currentVariant(state);
      if (!v.available) return;
      var pct = MR.bundle.tierPct(state.qty);
      var total = Math.round(v.price * state.qty * (1 - pct / 100));
      var base = btnText.getAttribute('data-default-text') || 'Ajouter au panier';
      btnText.textContent = state.qty > 1
        ? 'Ajouter ' + state.qty + ' pièces — ' + money(total)
        : base + ' — ' + money(v.price);
      if (state.qty > 1 && state.unitsOk === false) btn.setAttribute('disabled', 'disabled');
      else btn.removeAttribute('disabled');
      document.dispatchEvent(new CustomEvent('mr:atc-label', { detail: { text: btnText.textContent } }));
    },

    onSubmit: function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var root = $('[data-mr-bundle][data-form-id="' + form.getAttribute('id') + '"]');
      if (!root || !root._mr) return;
      var state = root._mr;
      if (state.qty <= 1) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      if (!MR.bundle.validateUnits(state)) return;

      var mainId = String(MR.bundle.currentVariant(state).id);
      var counts = {};
      counts[mainId] = 1;
      MR.bundle.readUnits(state).forEach(function (opts) {
        var v = MR.bundle.findVariant(state, opts);
        if (v) counts[v.id] = (counts[v.id] || 0) + 1;
      });
      var items = Object.keys(counts).map(function (id) { return { id: parseInt(id, 10), quantity: counts[id] }; });
      var btn = form.querySelector('[data-add-to-cart]');
      if (btn) btn.classList.add('btn--loading');

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      }).then(function (r) { return r.json(); }).then(function (data) {
        if (btn) btn.classList.remove('btn--loading');
        if (data.status) {
          var err = form.querySelector('.errors') || document.createElement('div');
          err.className = 'errors text-center';
          err.textContent = data.description || data.message || 'Erreur';
          form.appendChild(err);
          return;
        }
        var item = data.items ? data.items[data.items.length - 1] : data;
        form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: item, addToCartBtn: btn }, bubbles: true
        }));
      }).catch(function () { if (btn) btn.classList.remove('btn--loading'); });
    }
  };

  /* ---------------------------------------------------------- cart extras */
  MR.cart = {
    init: function () {
      var self = this;
      if (!$('[data-mr-cart-extras]')) return;
      ['ajaxProduct:added', 'cart:updated', 'mr:cart:built'].forEach(function (ev) {
        document.addEventListener(ev, function () { setTimeout(function () { self.refresh(); }, 350); });
      });
      // Impulse rebuilds drawer markup after qty changes — hook it so we stay in sync.
      if (window.theme && theme.CartForm && theme.CartForm.prototype && !theme.CartForm.prototype._mrPatched) {
        var orig = theme.CartForm.prototype.cartMarkup;
        theme.CartForm.prototype.cartMarkup = function () {
          var r = orig.apply(this, arguments);
          document.dispatchEvent(new CustomEvent('mr:cart:built'));
          return r;
        };
        theme.CartForm.prototype._mrPatched = true;
      }
      document.addEventListener('click', function (e) {
        var add = e.target.closest('[data-mr-quick-add]');
        if (!add) return;
        e.preventDefault();
        add.classList.add('is-loading');
        fetch('/cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(add.getAttribute('data-mr-quick-add'), 10), quantity: 1 }] })
        }).then(function (r) { return r.json(); }).then(function () {
          add.classList.remove('is-loading');
          document.dispatchEvent(new CustomEvent('cart:build'));
          self.refresh();
        });
      });
      self.refresh();
    },

    refresh: function () {
      getCart().then(function (cart) { MR.cart.render(cart); });
    },

    render: function (cart) {
      var total = cart.total_price;
      var qty = cart.item_count;

      $$('[data-mr-subtotal]').forEach(function (el) { el.textContent = money(total); });

      // Celebrate unlocks (free shipping / new tier / gift) — only after the first render.
      var prev = MR.cart._prev;
      var tierNow = 0; (S.tiers || []).forEach(function (t) { if (qty >= t.qty) tierNow = t.pct; });
      if (prev) {
        var drawerBar = $('#CartDrawer [data-mr-ship]') || $('[data-mr-ship]');
        if (prev.total < S.freeShipping && total >= S.freeShipping) MR.fx.burst(drawerBar, { n: 34 });
        else if (S.giftThreshold && prev.total < S.giftThreshold && total >= S.giftThreshold) MR.fx.burst(drawerBar, { n: 34 });
        if (tierNow > prev.tier) MR.fx.burst($('#CartDrawer [data-mr-ladder]') || $('[data-mr-ladder]'), { n: 26 });
      }
      MR.cart._prev = { total: total, tier: tierNow };

      $$('[data-mr-cart-extras]').forEach(function (root) {
        root.classList.toggle('is-empty', qty === 0);

        // Free shipping + gift milestones
        var bar = $('[data-mr-ship]', root);
        if (bar) {
          var goal = S.freeShipping;
          var max = Math.max(goal, S.giftThreshold || 0);
          var fill = $('[data-mr-ship-fill]', bar);
          var msg = $('[data-mr-ship-msg]', bar);
          if (fill) fill.style.transform = 'scaleX(' + Math.min(1, total / max) + ')';
          var marks = $$('[data-mr-mark]', bar);
          marks.forEach(function (m) {
            var at = parseInt(m.getAttribute('data-mr-mark'), 10);
            m.style.left = Math.min(100, at / max * 100) + '%';
            m.classList.toggle('is-done', total >= at);
          });
          if (msg) {
            if (total < goal) msg.innerHTML = fmt(T.addOne, { amount: '<strong>' + money(goal - total) + '</strong>' });
            else if (S.giftThreshold && total < S.giftThreshold) msg.innerHTML = (goal <= 0 ? '✓ ' + T.shipDone + ' · ' : '') + fmt(T.giftLeft, { amount: '<strong>' + money(S.giftThreshold - total) + '</strong>', gift: S.giftLabel });
            else if (S.giftThreshold) msg.innerHTML = '✓ ' + T.shipDone + ' · ' + fmt(T.giftDone, { gift: S.giftLabel });
            else msg.innerHTML = '✓ ' + T.shipDone;
          }
        }

        // Gift: show add button once threshold is met and gift not yet in cart
        var giftBtn = $('[data-mr-gift]', root);
        if (giftBtn) {
          var gid = giftBtn.getAttribute('data-product-id');
          var hasGift = cart.items.some(function (it) { return String(it.product_id) === gid; });
          giftBtn.hidden = !(S.giftThreshold && cart.total_price >= S.giftThreshold && !hasGift);
        }

        // Bundle ladder
        var ladder = $('[data-mr-ladder]', root);
        if (ladder && S.bundleOn) {
          var tiers = S.tiers || [];
          var next = null, cur = 0;
          tiers.forEach(function (t) { if (qty >= t.qty) cur = t.pct; else if (!next) next = t; });
          $$('[data-mr-step]', ladder).forEach(function (s) {
            s.classList.toggle('is-done', qty >= parseInt(s.getAttribute('data-mr-step'), 10));
          });
          var lmsg = $('[data-mr-ladder-msg]', ladder);
          if (lmsg) {
            if (next) {
              var n = next.qty - qty;
              lmsg.innerHTML = fmt(T.tierNext, { n: n, s: n > 1 ? 's' : '', pct: next.pct });
            } else {
              lmsg.innerHTML = fmt(T.tierMax, { pct: cur });
            }
          }
          ladder.hidden = qty === 0;
        }

        // Savings line
        var save = $('[data-mr-savings]', root);
        if (save) {
          var disc = cart.total_discount || 0;
          save.hidden = disc <= 0;
          var amt = $('[data-mr-savings-amount]', save);
          if (amt) amt.textContent = money(disc);
        }

        // Hide upsells already in cart
        var inCart = {};
        cart.items.forEach(function (it) { inCart[it.product_id] = true; });
        var shown = 0;
        $$('[data-mr-upsell-item]', root).forEach(function (u) {
          var hide = inCart[u.getAttribute('data-product-id')] || shown >= 3;
          u.hidden = !!hide;
          if (!hide) shown++;
        });
        var hint = $('[data-mr-upsell-hint]', root);
        if (hint) {
          var nt = null; (S.tiers || []).forEach(function (t) { if (!nt && qty < t.qty) nt = t; });
          hint.innerHTML = nt ? (nt.qty - qty === 1 ? '+1 pièce = <b>−' + nt.pct + ' % sur tout</b>' : '+' + (nt.qty - qty) + ' pièces = <b>−' + nt.pct + ' %</b>') : '';
        }
        var up = $('[data-mr-upsell]', root);
        if (up) up.hidden = shown === 0 || qty === 0;
      });
    }
  };

  /* ----------------------------------------------------------- sticky ATC */
  MR.sticky = {
    init: function () {
      var bar = $('[data-mr-sticky]');
      if (!bar) return;
      var form = document.getElementById(bar.getAttribute('data-form-id'));
      if (!form) return;
      var mainBtn = form.querySelector('[data-add-to-cart]');
      var label = $('[data-mr-sticky-label]', bar);
      var price = $('[data-mr-sticky-price]', bar);
      var variantEl = $('[data-mr-sticky-variant]', bar);
      var btn = $('[data-mr-sticky-btn]', bar);

      function sync() {
        var t = form.querySelector('[data-add-to-cart-text]');
        if (label && t) label.textContent = t.textContent.trim();
        var p = document.querySelector('[data-product-price]');
        if (price && p) price.textContent = p.textContent.trim();
        var checked = $$('input[type="radio"][form="' + form.getAttribute('id') + '"]:checked').map(function (i) { return i.value; });
        if (variantEl) variantEl.textContent = checked.join(' · ');
        if (btn && mainBtn) btn.disabled = mainBtn.disabled;
      }
      document.addEventListener('variant:change', function () { setTimeout(sync, 30); });
      document.addEventListener('mr:atc-label', sync);
      sync();

      btn.addEventListener('click', function () {
        if (form.requestSubmit) form.requestSubmit(mainBtn);
        else mainBtn.click();
      });

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            var past = !e.isIntersecting && e.boundingClientRect.top < 0;
            bar.classList.toggle('is-visible', past);
            document.documentElement.classList.toggle('mr-sticky-on', past);
          });
        });
        io.observe(mainBtn);
      }
    }
  };

  /* ------------------------------------------------------ delivery estimate */
  MR.delivery = {
    addBusinessDays: function (date, n) {
      var d = new Date(date);
      while (n > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) n--;
      }
      return d;
    },
    init: function () {
      var els = $$('[data-mr-delivery]');
      if (!els.length) return;
      var fmtDate = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
      function render() {
        var now = new Date();
        var cutoff = new Date(now);
        cutoff.setHours(S.cutoffHour || 23, 0, 0, 0);
        var weekend = now.getDay() === 0 || now.getDay() === 6;
        var start = (now > cutoff || weekend) ? MR.delivery.addBusinessDays(now, 1) : now;
        var from = MR.delivery.addBusinessDays(start, S.shipMin || 3);
        var to = MR.delivery.addBusinessDays(start, S.shipMax || 6);
        var left = cutoff - now;
        els.forEach(function (el) {
          var range = $('[data-mr-delivery-range]', el);
          var cd = $('[data-mr-delivery-cutoff]', el);
          if (range) range.textContent = fmtDate.format(from) + ' – ' + fmtDate.format(to);
          if (cd) {
            if (left > 0 && !weekend) {
              var h = Math.floor(left / 36e5), m = Math.floor(left / 6e4) % 60;
              cd.innerHTML = 'Commandez dans <strong>' + (h ? h + ' h ' : '') + m + ' min</strong> pour une expédition aujourd’hui';
            } else {
              cd.innerHTML = 'Expédition dès le prochain jour ouvré';
            }
          }
        });
      }
      if (S.xmas) {
        var xd = new Date(S.xmas + 'T23:59:00');
        if (!isNaN(xd) && new Date() <= xd) {
          var fx = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(xd);
          $$('[data-mr-xmas]').forEach(function (r) { r.hidden = false; $('[data-mr-xmas-date]', r).textContent = fx; });
        }
      }
      render();
      setInterval(render, 30000);
    }
  };

  /* ------------------------------------------- FBT "Complétez votre look" */
  MR.fbt = {
    init: function () {
      $$('[data-mr-fbt]').forEach(function (root) {
        if (root._mr) return;
        root._mr = true;
        var main = $('[data-mr-fbt-main]', root);
        var form = document.querySelector('form.product-single__form');

        function mainPrice() {
          var b = $('[data-mr-bundle]');
          var v = b && b._mr ? MR.bundle.currentVariant(b._mr) : null;
          return v ? { p: v.price, c: Math.max(v.compare_at_price || 0, v.price) }
                   : { p: parseInt(main.getAttribute('data-price'), 10), c: parseInt(main.getAttribute('data-compare'), 10) };
        }

        function update() {
          var m = mainPrice();
          var total = m.p, compare = m.c, n = 1;
          $$('[data-mr-fbt-item]', root).forEach(function (cb) {
            var sel = cb.closest('.mr-fbt__item').querySelector('[data-mr-fbt-variant]');
            if (sel) {
              var o = sel.options[sel.selectedIndex];
              cb.setAttribute('data-variant', sel.value);
              cb.setAttribute('data-price', o.getAttribute('data-price'));
              cb.setAttribute('data-compare', o.getAttribute('data-compare'));
            }
            if (!cb.checked) return;
            n++;
            total += parseInt(cb.getAttribute('data-price'), 10);
            compare += Math.max(parseInt(cb.getAttribute('data-compare'), 10), parseInt(cb.getAttribute('data-price'), 10));
          });
          var pct = MR.bundle.tierPct(n);
          var final = Math.round(total * (1 - pct / 100));
          $('[data-mr-fbt-count]', root).textContent = n;
          $('[data-mr-fbt-total]', root).textContent = money(final);
          $('[data-mr-fbt-compare]', root).textContent = compare > final ? money(compare) : '';
          $('[data-mr-fbt-save]', root).textContent = compare > final
            ? 'Vous économisez ' + money(compare - final) + (pct ? ' (dont −' + pct + ' % offre progressive)' : '') : '';
        }

        root.addEventListener('change', update);
        document.addEventListener('variant:change', function () { setTimeout(update, 30); });
        update();

        $('[data-mr-fbt-add]', root).addEventListener('click', function (e) {
          var btn = e.currentTarget;
          var mainId = form ? (form.querySelector('[name="id"]') || {}).value : null;
          var items = [];
          if (mainId) items.push({ id: parseInt(mainId, 10), quantity: 1 });
          $$('[data-mr-fbt-item]:checked', root).forEach(function (cb) {
            items.push({ id: parseInt(cb.getAttribute('data-variant'), 10), quantity: 1 });
          });
          btn.classList.add('btn--loading');
          fetch('/cart/add.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: items })
          }).then(function (r) { return r.json(); }).then(function (data) {
            btn.classList.remove('btn--loading');
            if (data.status) { alert(data.description || data.message); return; }
            document.dispatchEvent(new CustomEvent('cart:build'));
            document.dispatchEvent(new CustomEvent('cart:open'));
            MR.cart.refresh();
          }).catch(function () { btn.classList.remove('btn--loading'); });
        });
      });
    }
  };

  /* ---------------------------------------------------- "Les Tenues" sets */
  MR.outfits = {
    init: function () {
      $$('[data-mr-outfit-tab]').forEach(function (tab) {
        if (tab._mr) return; tab._mr = true;
        tab.addEventListener('click', function () {
          var sec = tab.closest('section');
          var i = tab.getAttribute('data-mr-outfit-tab');
          $$('[data-mr-outfit-tab]', sec).forEach(function (t) { var on = t === tab; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on); });
          $$('[data-mr-outfit]', sec).forEach(function (o) {
            var on = o.getAttribute('data-mr-outfit') === i;
            o.hidden = !on; o.classList.toggle('is-active', on);
          });
        });
      });
      $$('[data-mr-outfit-panel]').forEach(function (panel) {
        if (panel._mr) return; panel._mr = true;
        function vals(row) {
          var sel = $('[data-mr-oi-variant]', row);
          var o = sel.tagName === 'SELECT' ? sel.options[sel.selectedIndex] : sel;
          return { id: sel.value, p: parseInt(o.getAttribute('data-price'), 10), c: parseInt(o.getAttribute('data-compare'), 10), img: o.getAttribute('data-img') };
        }
        function update() {
          var n = 0, total = 0, compare = 0;
          $$('[data-mr-oi]', panel).forEach(function (row) {
            var on = $('[data-mr-oi-on]', row).checked;
            row.classList.toggle('is-off', !on);
            var v = vals(row);
            var pe = $('[data-mr-oi-price]', row); if (pe) pe.textContent = money(v.p);
            var im = $('[data-mr-oi-img]', row); if (im && v.img) im.src = v.img;
            if (!on) return;
            n++; total += v.p; compare += Math.max(v.c, v.p);
          });
          var pct = MR.bundle.tierPct(n);
          var final = Math.round(total * (1 - pct / 100));
          $('[data-mr-o-total]', panel).textContent = money(final);
          $('[data-mr-o-compare]', panel).textContent = compare > final ? money(compare) : '';
          $('[data-mr-o-pct]', panel).textContent = pct ? '(−' + pct + ' % inclus)' : '';
          var gift = S.giftThreshold && final >= S.giftThreshold ? ' + ' + (S.giftLabel || 'cadeau offert') : '';
          $('[data-mr-o-save]', panel).textContent = compare > final ? 'Vous économisez ' + money(compare - final) + gift : gift;
          $('[data-mr-o-add]', panel).disabled = n === 0;
        }
        panel.addEventListener('change', update);
        update();
        $('[data-mr-o-add]', panel).addEventListener('click', function (e) {
          var btn = e.currentTarget, items = [];
          $$('[data-mr-oi]', panel).forEach(function (row) {
            if ($('[data-mr-oi-on]', row).checked) items.push({ id: parseInt(vals(row).id, 10), quantity: 1 });
          });
          if (!items.length) return;
          btn.classList.add('btn--loading');
          fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: items }) })
            .then(function (r) { return r.json(); })
            .then(function (d) {
              btn.classList.remove('btn--loading');
              if (d.status) { alert(d.description || d.message); return; }
              MR.fx && MR.fx.burst(btn);
              document.dispatchEvent(new CustomEvent('cart:build'));
              document.dispatchEvent(new CustomEvent('cart:open'));
              MR.cart.refresh();
            }).catch(function () { btn.classList.remove('btn--loading'); });
        });
      });
    }
  };

  /* -------------------------------------------------------- fx: confetti */
  MR.fx = {
    burst: function (el, opts) {
      if (reduceMotion || !el) return;
      var r = el.getBoundingClientRect();
      var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      var colors = ['#e2471b', '#0f1419', '#f4f0e8', '#ff8a3d', '#8fbcdb'];
      var n = (opts && opts.n) || 28;
      for (var i = 0; i < n; i++) {
        var p = document.createElement('i');
        p.className = 'mr-confetti';
        var ang = Math.random() * Math.PI * 2, dist = 60 + Math.random() * 110;
        p.style.cssText = 'left:' + cx + 'px;top:' + cy + 'px;background:' + colors[i % colors.length] +
          ';--dx:' + Math.cos(ang) * dist + 'px;--dy:' + (Math.sin(ang) * dist - 60) + 'px;--rot:' + (Math.random() * 720 - 360) + 'deg';
        document.body.appendChild(p);
        setTimeout(function (x) { return function () { x.remove(); }; }(p), 1100);
      }
    }
  };

  /* -------------------------------------------- smart default sizes in selects */
  MR.smartDefaults = function (root) {
    var saved = (MR.size && MR.size.get && MR.size.get()) || null;
    var want = saved && saved.size ? saved.size : 'M';
    $$('select[data-mr-oi-variant], select[data-mr-fbt-variant]', root || document).forEach(function (sel) {
      if (sel._mrDef) return; sel._mrDef = true;
      var cur = sel.options[sel.selectedIndex];
      var colour = cur ? cur.text.split(' / ')[0] : '';
      var best = null;
      [].slice.call(sel.options).forEach(function (o) {
        if (o.disabled || best) return;
        var parts = o.text.split(' / ');
        var size = parts[parts.length - 1].trim();
        if (parts.length > 1 && parts[0] === colour && (size === want || size === '42' || size === '42 ')) best = o;
      });
      if (best) { sel.value = best.value; sel.dispatchEvent(new Event('change', { bubbles: true })); }
    });
  };

  /* ------------------------------------- card swatches: one scrollable row */
  MR.cardSwatches = function () {
    $$('.grid-product__colors').forEach(function (wrap) {
      if (wrap._mr) return; wrap._mr = true;
      /* swipe on the swatch row must not open the product link */
      wrap.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });
    });
  };

  /* ------------------------------------------------------------- tabs/acc */
  MR.tabs = {
    init: function () {
      document.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-mr-tab]');
        if (!tab) return;
        var group = tab.closest('[data-mr-tabs]');
        if (!group) return;
        var id = tab.getAttribute('data-mr-tab');
        $$('[data-mr-tab]', group).forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        $$('[data-mr-panel]', group).forEach(function (p) { p.hidden = p.getAttribute('data-mr-panel') !== id; });
      });
    }
  };

  /* ------------------------------------------------- ad landing offer bar */
  MR.adbar = {
    init: function () {
      var bar = $('[data-mr-adbar]');
      if (!bar) return;
      document.body.classList.add('mr-ads');
      var ticking = false;
      function u() { bar.classList.toggle('is-on', window.scrollY > 520 && !document.documentElement.classList.contains('js-drawer-open')); ticking = false; }
      window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(u); } }, { passive: true });
      u();
    }
  };

  /* ----------------------------------------------------------------- boot */
  /* ------------------------------------------------------ scroll progress */
  MR.progress = {
    init: function () {
      if (reduceMotion || $('.mr-progress')) return;
      var bar = document.createElement('div'); bar.className = 'mr-progress'; document.body.appendChild(bar);
      var ticking = false;
      function u() { var h = document.documentElement; var max = h.scrollHeight - h.clientHeight; bar.style.transform = 'scaleX(' + (max > 0 ? h.scrollTop / max : 0) + ')'; ticking = false; }
      window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(u); } }, { passive: true });
      u();
    }
  };

  function boot() {
    MR.motion.init();
    MR.progress.init();
    MR.countdown.init();
    MR.weather.init();
    MR.modal.init();
    MR.tabs.init();
    $$('[data-mr-quiz-page] [data-mr-quiz], [data-mr-quiz-page][data-mr-quiz]').forEach(function (q) { MR.size.initQuiz(q); });
    if ($('[data-mr-bundle]') || $('fieldset.variant-input-wrap')) MR.size.initProduct();
    MR.bundle.init();
    MR.delivery.init();
    MR.sticky.init();
    MR.smartDefaults();
    MR.cardSwatches();
    MR.adbar.init();
    MR.fbt.init();
    MR.outfits.init();
    // theme.js is deferred too; wait one tick so theme.CartForm exists.
    setTimeout(function () { MR.cart.init(); }, 0);
  }

  onReady(boot);

  // Theme editor: re-run motion/quiz when sections reload.
  document.addEventListener('shopify:section:load', function () {
    MR.motion.init();
    MR.countdown.init();
    MR.weather.init();
    $$('[data-mr-quiz]').forEach(function (q) { if (q.closest('[data-mr-quiz-page]')) MR.size.initQuiz(q); });
    $$('[data-mr-bundle]').forEach(function (root) { MR.bundle.setup(root); });
  });
})();
